import { useCallback, useEffect, useRef, useState } from 'react'

/** Aufnahmeformat-Kaskade: Chrome/Firefox/Android können webm/opus,
 * iOS/macOS-Safari NUR audio/mp4 (AAC) — webm-Recording existiert dort
 * nicht. Der tatsächlich gewählte Typ wandert mit ins Attachment, damit
 * Playback + Whisper den echten Container kennen. */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m
  }
  return ''
}

export function recordingFilename(mimeType: string): string {
  return mimeType.startsWith('audio/mp4') ? 'sprachnachricht.m4a' : 'sprachnachricht.webm'
}

export interface AudioRecording {
  blob: Blob
  mimeType: string
  durationMs: number
}

/**
 * MediaRecorder-Wrapper für Sprachnachrichten: start → (Timer läuft) →
 * stop liefert das fertige Recording, cancel verwirft es. getUserMedia
 * braucht HTTPS (Prod ✓) bzw. localhost und funktioniert in der
 * iOS-PWA-Standalone. `supported` ist false, wo MediaRecorder fehlt —
 * die UI blendet den Mikrofon-Button dann aus.
 */
export function useAudioRecorder() {
  const [recording, setRecording] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const startedAtRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const supported =
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
    const rec = recorderRef.current
    recorderRef.current = null
    if (rec) {
      for (const track of rec.stream.getTracks()) track.stop()
    }
    chunksRef.current = []
    setRecording(false)
    setElapsedMs(0)
  }, [])

  // Beim Unmount (Chat-Wechsel etc.) Mikro sicher freigeben.
  useEffect(() => cleanup, [cleanup])

  const start = useCallback(async () => {
    setError(null)
    if (!supported) {
      setError('Aufnahme wird von diesem Browser nicht unterstützt')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = pickMimeType()
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunksRef.current = []
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      rec.start(250)
      recorderRef.current = rec
      startedAtRef.current = Date.now()
      setElapsedMs(0)
      setRecording(true)
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current)
      }, 200)
    } catch (err) {
      const name = (err as DOMException).name
      setError(
        name === 'NotAllowedError'
          ? 'Mikrofon-Zugriff verweigert — bitte in den Browser-Einstellungen erlauben'
          : `Aufnahme fehlgeschlagen: ${(err as Error).message}`,
      )
    }
  }, [supported])

  /** Beendet die Aufnahme und liefert das Ergebnis (null wenn leer). */
  const stop = useCallback((): Promise<AudioRecording | null> => {
    const rec = recorderRef.current
    if (!rec || rec.state === 'inactive') {
      cleanup()
      return Promise.resolve(null)
    }
    const durationMs = Date.now() - startedAtRef.current
    return new Promise((resolve) => {
      rec.onstop = () => {
        // rec.mimeType ist der tatsächlich verwendete Typ (kann vom
        // requested abweichen); Fallback für Browser, die ihn leer lassen.
        const mimeType = rec.mimeType || pickMimeType() || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: mimeType })
        cleanup()
        resolve(blob.size > 0 ? { blob, mimeType, durationMs } : null)
      }
      rec.stop()
    })
  }, [cleanup])

  const cancel = useCallback(() => {
    const rec = recorderRef.current
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null
      rec.stop()
    }
    cleanup()
  }, [cleanup])

  return { supported, recording, elapsedMs, error, start, stop, cancel }
}
